/**
 * One-time hard reload recovery when MapLibre image assets fail to load
 * (stale/corrupt cache, sprite decode errors, etc.).
 */

import type { MapLibreEvent, Map as MapLibreMap } from "maplibre-gl";

import {
    STOP_CHILD_ICON_IMAGE_ID,
    STOP_PARENT_ICON_IMAGE_ID,
} from "./mapLayerIds";

const MAP_IMAGE_RELOAD_SESSION_KEY = "flowride.map-image-reload";

/**
 * Map image ids registered asynchronously by FlowRide after style load.
 * `styleimagemissing` can fire while these are still loading — ignore them.
 */
const FLOWRIDE_ASYNC_MAP_IMAGE_IDS = new Set( [
    "user-marker-icon",
    "heading-cone-icon",
    "navigation-guide",
    "itinerary-walk-dot-sdf",
    STOP_PARENT_ICON_IMAGE_ID,
    STOP_CHILD_ICON_IMAGE_ID,
] );

/** In-memory guard so parallel failures trigger only one reload per page load. */
let mapImageReloadScheduled = false;

const registeredMaps = new WeakSet<MapLibreMap>();

const MAP_IMAGE_ERROR_PATTERN = /image|sprite|could not be loaded/i;

type MapLoadImageResult = Awaited<ReturnType<MapLibreMap["loadImage"]>>;

/**
 * Clears Cache API entries and navigates with a cache-busting query param.
 */
async function performMapImageHardReload(): Promise<void> {
    if ( "caches" in window ) {
        try {
            const cacheNames = await caches.keys();
            await Promise.all( cacheNames.map( ( cacheName ) => caches.delete( cacheName ) ) );
        } catch( cacheError: unknown ) {
            console.warn( "[MapImageFailureRecovery] Failed to clear caches before reload", cacheError );
        }
    }

    const url = new URL( window.location.href );
    url.searchParams.set( "_mapImageReload", Date.now().toString() );
    window.location.replace( url.toString() );
}

/**
 * Schedules a single hard reload for the current tab session.
 *
 * @param imageRef Image URL, sprite id, or diagnostic label.
 * @param error Failure reason when available.
 */
function scheduleMapImageHardReloadOnce( imageRef: string, error: unknown ): void {
    console.error( "[MapImageFailureRecovery] Map image failed to load:", imageRef, error );

    if ( mapImageReloadScheduled ) {
        return;
    }

    try {
        if ( sessionStorage.getItem( MAP_IMAGE_RELOAD_SESSION_KEY ) === "1" ) {
            return;
        }
        sessionStorage.setItem( MAP_IMAGE_RELOAD_SESSION_KEY, "1" );
    } catch {
        return;
    }

    mapImageReloadScheduled = true;
    void performMapImageHardReload();
}

/**
 * Returns whether a MapLibre `error` event likely relates to image/sprite loading.
 *
 * @param error Error carried by the map `error` event.
 */
function isMapImageRelatedError( error: unknown ): boolean {
    if ( !( error instanceof Error ) ) {
        return false;
    }

    return MAP_IMAGE_ERROR_PATTERN.test( error.message );
}

/**
 * Registers global MapLibre image failure recovery on a map instance:
 * - wraps `map.loadImage` rejections,
 * - ignores basemap `styleimagemissing` (dynamic shields, empty ids, zoom hacks),
 * - listens to `error` for image/sprite load failures.
 *
 * Safe to call multiple times on the same map; registration runs once per instance.
 *
 * @param map Active MapLibre map instance.
 */
export function registerMapImageFailureRecovery( map: MapLibreMap ): void {
    if ( registeredMaps.has( map ) ) {
        return;
    }
    registeredMaps.add( map );

    const originalLoadImage = map.loadImage.bind( map );
    map.loadImage = ( url: string ): ReturnType<MapLibreMap["loadImage"]> => {
        return originalLoadImage( url ).catch( ( error: unknown ) => {
            scheduleMapImageHardReloadOnce( url, error );

            // Never resolves: avoids unhandled rejections and unsafe `.then` handlers.
            return new Promise<MapLoadImageResult>( () => {
                // Intentionally never resolves.
            } );
        } );
    };

    map.on( "styleimagemissing", ( event: MapLibreEvent<"styleimagemissing"> & { "id": string } ) => {
        // FlowRide icons load asynchronously after style.load; MapLibre may request them first.
        if ( FLOWRIDE_ASYNC_MAP_IMAGE_IDS.has( event.id ) ) {
            return;
        }

        /*
         * MapTiler basemap gaps are expected and must not trigger reload:
         * - dynamic shields (`road_`, `CH-highway_`, …) when ref metadata is empty,
         * - intentional zoom hacks (`" "` on Village/Town labels at z≥12),
         * - empty icon-image fallbacks (`""` on Food POIs without class).
         * No placeholder — MapLibre skips the symbol; transparent 1×1 RGBA renders black.
         */
    } );

    map.on( "error", ( event: MapLibreEvent<"error"> & { "error"?: Error } ) => {
        if ( event.error === undefined || !isMapImageRelatedError( event.error ) ) {
            return;
        }
        scheduleMapImageHardReloadOnce( event.error.message, event.error );
    } );
}
